import { requestHeaders } from '../helpers/csrf';
import { readResponsePayload, resolveErrorMessage } from '../helpers/error-handler';

/**
 * BaseModalViewer — Base para modais de visualização de dados da nova área.
 *
 * Fornece:
 *   - Instanciação de dialogService / notificationService
 *   - open() com configuração via config
 *   - openFromRow() com _rowToOptions() sobrescrevível
 *   - _fetchAndRender() para buscar e renderizar dados assíncronos
 *   - _renderLoading / _renderError / _renderEmpty com selector parametrizável
 *   - _bindCloseBtn() para `[data-viewer-close]`
 *   - _escapeHtml() utilitário
 *
 * Uso:
 *   class MeuModal extends BaseModalViewer {
 *       constructor() {
 *           super({ className: 'nd-meu-dialog', size: 'md' });
 *       }
 *       _buildLayout(options) { ... }
 *       _rowToOptions(row) { return { fetchUrl: row.routes.data }; }
 *   }
 */
class BaseModalViewer {
    constructor(config = {}) {
        this.dialogService = globalThis.FlyServices?.dialog;
        this.notificationService = globalThis.FlyServices?.notify;
        this.config = config;
        this._dialogRef = null;
    }

    open(options = {}) {
        if (!this.dialogService) return null;

        const root = this._buildLayout(options);

        this._dialogRef = this.dialogService.open({
            content: root,
            size: this.config.size ?? 'nova-area',
            position: 'center',
            className: this.config.className ?? '',
            showClose: this.config.showClose ?? false,
            animation: this.config.animation ?? 'scale',
            closeOnOverlayClick: this.config.closeOnOverlayClick ?? true,
        });

        this._bindCloseBtn(root);
        this._bindEvents(root, options);
        this._focus(root);

        return this._dialogRef;
    }

    openFromRow(row = {}) {
        return this.open(this._rowToOptions(row));
    }

    // ─── Template methods — sobrescreva nas subclasses ─────────────────

    _buildLayout(options) {
        return document.createElement('div');
    }

    _bindEvents(root, options) { /* noop — sobrescreva na subclasse */ }

    _rowToOptions(row) {
        return row;
    }

    _renderData(root, payload) { /* noop — sobrescreva na subclasse */ }

    // ─── Fetch helpers ────────────────────────────────────────────────

    /**
     * Busca fetchUrl e delega renderização a _renderData().
     * @param {Element} root
     * @param {string}  fetchUrl
     * @param {string}  bodySelector  Seletor do tbody/container dos itens.
     * @param {number}  [colSpan=1]
     * @param {string}  [emptyMessage]
     */
    async _fetchAndRender(root, fetchUrl, { bodySelector, colSpan = 1, emptyMessage = 'Nenhum item encontrado.' } = {}) {
        if (!fetchUrl) {
            this._renderEmpty(root, bodySelector, colSpan, emptyMessage);
            return;
        }

        this._renderLoading(root, bodySelector, colSpan);

        try {
            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: requestHeaders({ Accept: 'application/json' }),
            });

            const payload = await readResponsePayload(response);

            if (!response.ok) {
                const message = resolveErrorMessage(payload, 'Erro ao carregar os dados.');
                this._renderError(root, bodySelector, message, colSpan);
                return;
            }

            this._renderData(root, payload);
        } catch (err) {
            console.error(`[${this.constructor.name}._fetchAndRender]`, err);
            this._renderError(root, bodySelector, 'Erro de conexão ao carregar os dados.', colSpan);
        }
    }

    // ─── Rendering helpers ────────────────────────────────────────────

    _renderLoading(root, selector, colSpan = 1) {
        const body = root.querySelector(selector);
        if (!body) return;
        body.innerHTML = `<tr><td colspan="${colSpan}" class="nd-viewer__state">Carregando...</td></tr>`;
    }

    _renderError(root, selector, message, colSpan = 1) {
        const body = root.querySelector(selector);
        if (!body) return;
        body.innerHTML = `<tr><td colspan="${colSpan}" class="nd-viewer__state nd-viewer__state--error">${this._escapeHtml(message)}</td></tr>`;
    }

    _renderEmpty(root, selector, colSpan = 1, message = 'Nenhum item encontrado.') {
        const body = root.querySelector(selector);
        if (!body) return;
        body.innerHTML = `<tr><td colspan="${colSpan}" class="nd-viewer__state">${this._escapeHtml(message)}</td></tr>`;
    }

    // ─── Utilities ────────────────────────────────────────────────────

    /**
     * Vincula o botão de fechar com atributo `[data-viewer-close]`.
     * Subclasses podem sobrescrever para usar outro atributo.
     * @param {Element} root
     */
    _bindCloseBtn(root) {
        root.querySelector('[data-viewer-close]')?.addEventListener('click', () => this._dialogRef?.close());
    }

    _focus(root) {
        requestAnimationFrame(() => {
            root.querySelector('input,button,select,textarea')?.focus();
        });
    }

    _escapeHtml(value) {
        return String(value ?? '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#39;');
    }
}

export default BaseModalViewer;
